import { RefObject, useEffect } from 'react';
import { useEventCallback } from '../utils/useEventCallback';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export type ModalDialogOptions = {
  active: boolean;
  /** Omit when the modal already closes on Escape itself. */
  onEscape?: () => void;
  /** Where focus goes on close; the element focused on open otherwise. */
  returnFocusRef?: RefObject<HTMLElement | null>;
};

/**
 * Makes `root` a real modal while `active`: everything outside it is
 * `inert`, focus moves in and Tab cycles within it, and focus returns on
 * close. The inert page is what lets a dialog claim `aria-modal`.
 */
export const useModalDialog = (
  // An element rather than a ref: a portalled root mounts after its owner, and
  // the effect has to run again when it arrives.
  root: HTMLElement | null,
  { active, onEscape, returnFocusRef }: ModalDialogOptions,
): void => {
  const handleEscape = useEventCallback(onEscape);
  const hasEscape = !!onEscape;

  useEffect(() => {
    if (!active || !root) return;

    const returnTo =
      returnFocusRef?.current ?? (document.activeElement as HTMLElement | null);

    // Every sibling of every ancestor, up to <body>: the dialog's own branch
    // stays live and nothing else does.
    const madeInert: Element[] = [];
    for (
      let node: Element = root;
      node.parentElement && node !== document.body;
      node = node.parentElement
    ) {
      for (const sibling of Array.from(node.parentElement.children)) {
        if (sibling === node || sibling.hasAttribute('inert')) continue;
        sibling.setAttribute('inert', '');
        madeInert.push(sibling);
      }
    }

    const focusables = () =>
      Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        // A collapsed group keeps its rows in the DOM under `hidden`.
        (element) => !element.closest('[hidden]'),
      );
    focusables()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      // Content handled it first — an open listbox closes itself, not the dialog.
      if (event.defaultPrevented) return;
      if (event.key === 'Escape' && hasEscape) {
        handleEscape();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      if (event.shiftKey && (current === first || !root.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (current === last || !root.contains(current))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      madeInert.forEach((element) => element.removeAttribute('inert'));
      if (returnTo?.isConnected) returnTo.focus();
    };
  }, [active, root, returnFocusRef, hasEscape, handleEscape]);
};
